<template>
    <div class="comp-section">
        <stats-grid :count="cardCount" :expanded="expandedCard" :row-height="320" :expanded-row-height="480">
            <stat-card expandable :expanded="expandedCard === 0" @toggle:expand="toggleExpand(0)">
                <template #header>
                    <span>{{ isTeamView ? $t('statistics.avg_minutes_per_player') : $t('statistics.game_minutes') }}</span>
                    <span v-if="isTeamView && matches.length" class="comp-hdr-avg">{{ $t('statistics.avg_short') }}
                        <b :style="{ color: colors.teal2 }">{{ avgPlayed }}'</b>
                    </span>
                </template>
                <template v-if="matches.length">
                    <div class="comp-bars">
                        <v-tooltip v-for="m in matches" :key="m.id" top :color="tooltipBg"
                            content-class="comp-tt" class="comp-bar-slot">
                            <template #activator="{ on, attrs }">
                                <div class="comp-bar-col" v-on="on" v-bind="attrs">
                                    <div class="comp-bar-val">{{ round(m.minPlayed) }}</div>
                                    <div class="comp-bar-track">
                                        <div class="comp-bar-fill"
                                            :style="{ height: barFillHeight(m), background: minColor(m) }" />
                                    </div>
                                    <div class="comp-bar-lbl">{{ m.abbr }}</div>
                                </div>
                            </template>
                            <div class="comp-tt-inner">
                                <div class="comp-tt-date">{{ m.dateLabel }} · {{ m.opp }}</div>
                                <div class="comp-tt-row">
                                    <span class="comp-tt-dot" :style="{ background: colors.teal }" />{{ isTeamView ? $t('statistics.avg_short') : $t('statistics.played') }}
                                    <b>{{ round(m.minPlayed) }}' ({{ playedPct(m) }}%)</b>
                                </div>
                                <div class="comp-tt-row">
                                    <span class="comp-tt-dot comp-tt-dot--track" />{{ $t('statistics.match') }}
                                    <b>{{ m.minMatch }}'</b>
                                </div>
                                <div v-if="isTeamView" class="comp-tt-row">
                                    <span class="comp-tt-dot" :style="{ background: colors.lime }" />{{ $t('statistics.players') }}
                                    <b>{{ m.players }}</b>
                                </div>
                            </div>
                        </v-tooltip>
                    </div>
                    <div class="comp-legend">
                        <div class="comp-leg-item"><span class="comp-leg-sq" :style="{ background: colors.teal }" />{{ isTeamView ? $t('statistics.avg_minutes_short') : $t('statistics.player') }}</div>
                        <div class="comp-leg-item"><span class="comp-leg-sq comp-leg-sq--track" />{{ $t('statistics.match') }}</div>
                    </div>
                </template>
                <div v-else class="comp-empty">{{ $t('common.without_data') }}</div>
            </stat-card>

            <stat-card expandable :expanded="expandedCard === 1" @toggle:expand="toggleExpand(1)">
                <template #header>
                    <span>{{ isTeamView ? $t('statistics.team_avg_rpe') : $t('statistics.rpe_post_match') }}</span>
                    <span v-if="rpeMatches.length" class="comp-hdr-avg">{{ $t('statistics.avg_short') }}
                        <b :style="{ color: rpeColor(rpeAvg) }">{{ rpeAvg }}</b>
                    </span>
                </template>
                <template v-if="rpeMatches.length">
                    <div class="comp-chart-wrap">
                        <line-chart :labels="rpeChart.labels" :datasets="rpeChart.datasets" :min-y="0" :max-y="10"
                            :step-y="2" :zones="rpeZones" :styles="chartStyles" />
                    </div>
                    <div class="comp-legend">
                        <div class="comp-leg-item"><span class="comp-leg-dot" :style="{ background: colors.green }" />{{ $t('statistics.level_low') }} &le;5</div>
                        <div class="comp-leg-item"><span class="comp-leg-dot" :style="{ background: colors.amber }" />{{ $t('statistics.level_mid') }} 6&ndash;7</div>
                        <div class="comp-leg-item"><span class="comp-leg-dot" :style="{ background: colors.red }" />{{ $t('statistics.level_high') }} &ge;8</div>
                    </div>
                </template>
                <div v-else class="comp-empty">{{ $t('common.without_data') }}</div>
            </stat-card>

            <stat-card expandable :expanded="expandedCard === 2" @toggle:expand="toggleExpand(2)">
                <template #header>
                    <span>{{ isTeamView ? $t('statistics.team_avg_wellness') : $t('statistics.wellness_pre_match') }}</span>
                    <span v-if="wellMatches.length" class="comp-hdr-avg">{{ $t('statistics.avg_short') }}
                        <b :style="{ color: wellColor(wellAvg) }">{{ wellAvg }}</b>/25
                    </span>
                </template>
                <template v-if="wellMatches.length">
                    <div class="comp-chart-wrap">
                        <line-chart :labels="wellChart.labels" :datasets="wellChart.datasets" :min-y="5" :max-y="25"
                            :step-y="5" :zones="wellnessZones" :styles="chartStyles" />
                    </div>
                    <div class="comp-legend">
                        <div class="comp-leg-item"><span class="comp-leg-dot" :style="{ background: colors.teal }" />{{ $t('statistics.optimal') }} &ge;18</div>
                        <div class="comp-leg-item"><span class="comp-leg-dot" :style="{ background: colors.amber }" />{{ $t('statistics.regular') }} 12&ndash;17</div>
                        <div class="comp-leg-item"><span class="comp-leg-dot" :style="{ background: colors.red }" />{{ $t('common.wellnessStatus.bad') }} &lt;12</div>
                    </div>
                </template>
                <div v-else class="comp-empty">{{ $t('common.without_data') }}</div>
            </stat-card>

            <stat-card expandable :expanded="expandedCard === 3" @toggle:expand="toggleExpand(3)">
                <template #header>
                    <span>{{ isTeamView ? $t('statistics.most_used_players') : $t('statistics.participation') }}</span>
                    <span v-if="isTeamView && teamPlayers.length" class="comp-hdr-avg">{{ $t('statistics.total_minutes') }}
                        <b>{{ squadTotalMinutes }}'</b>
                    </span>
                </template>
                <template v-if="isTeamView">
                    <div v-if="teamPlayers.length" class="comp-team">
                        <div v-for="row in teamPlayers" :key="row.studentId" class="comp-team-row">
                            <span class="comp-team-name">
                                <truncated-text :text="row.name" />
                            </span>
                            <div class="comp-team-bar-wrap">
                                <span v-for="p in teamPlayerMarks" :key="p.key" class="comp-team-pline"
                                    :style="{ left: p.pos + '%' }" />
                                <div class="comp-team-bar comp-team-bar--filled" :style="{ width: playerBarWidth(row) }" />
                            </div>
                            <span class="comp-team-val">{{ round(row.minutes) }}'</span>
                        </div>
                    </div>
                    <div v-else class="comp-empty">{{ $t('common.without_data') }}</div>
                    <div v-if="teamPlayerMarks.length" class="comp-legend">
                        <div v-for="p in teamPlayerMarks" :key="p.key" class="comp-leg-item">
                            <span class="comp-leg-dash" />{{ p.label }}
                        </div>
                    </div>
                </template>
                <template v-else-if="matches.length">
                    <div class="comp-ring">
                        <doughnut-chart :labels="participationLabels" :datasets="participationDatasets"
                            :center-lines="ringCenterLines" :styles="ringStyles" />
                    </div>
                    <div class="comp-stat-row">
                        <div class="comp-stat">
                            <span class="comp-stat-val">{{ matchesCount }}</span>
                            <span class="comp-stat-lbl">{{ $t('statistics.matches_count') }}</span>
                        </div>
                        <div class="comp-stat">
                            <span class="comp-stat-val">{{ totalPlayed }}'</span>
                            <span class="comp-stat-lbl">{{ $t('statistics.played') }}</span>
                        </div>
                        <div class="comp-stat">
                            <span class="comp-stat-val">{{ avgPlayed }}'</span>
                            <span class="comp-stat-lbl">{{ $t('statistics.avg_short') }}</span>
                        </div>
                    </div>
                </template>
                <div v-else class="comp-empty">{{ $t('common.without_data') }}</div>
            </stat-card>

            <stat-card :class="{ 'comp-card--wide': !hasPeers && !isTeamView }" flush expandable
                :expanded="expandedCard === 4" @toggle:expand="toggleExpand(4)">
                <template #header>
                    <span>{{ $t('statistics.match_history') }}</span>
                </template>
                <div v-if="history.length" class="comp-hist-wrap">
                    <table class="comp-hist">
                        <thead>
                            <tr>
                                <th>{{ $t('statistics.rival') }}</th>
                                <th>{{ $t('common.date') }}</th>
                                <th v-if="!isTeamView">{{ $t('statistics.pos_short') }}</th>
                                <th>{{ isTeamView ? $t('statistics.avg_minutes_short') : $t('statistics.player') }}</th>
                                <th>{{ $t('statistics.effort') }}</th>
                                <th>{{ $t('statistics.wellness') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="m in history" :key="m.id">
                                <td class="comp-hist-opp">{{ m.opp }}</td>
                                <td>{{ m.dateLabel }}</td>
                                <td v-if="!isTeamView">
                                    <span v-if="m.position" class="comp-pos">{{ m.position }}</span>
                                    <span v-else>&mdash;</span>
                                </td>
                                <td>{{ round(m.minPlayed) }}'</td>
                                <td>
                                    <span v-if="m.rpe != null" :style="{ color: rpeColor(m.rpe), fontWeight: 700 }">{{ round1(m.rpe) }}</span>
                                    <span v-else>&mdash;</span>
                                </td>
                                <td>
                                    <span v-if="m.well != null" class="comp-hist-well">
                                        <span class="comp-leg-dot" :style="{ background: wellColor(m.well) }" />{{ round1(m.well) }}/25
                                    </span>
                                    <span v-else>&mdash;</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div v-else class="comp-empty">{{ $t('common.without_data') }}</div>
            </stat-card>

            <stat-card v-if="isTeamView" expandable :expanded="expandedCard === 5" @toggle:expand="toggleExpand(5)">
                <template #header>
                    <span>{{ $t('statistics.squad_summary') }}</span>
                </template>
                <div v-if="matches.length" class="comp-summary">
                    <div class="comp-stat-row">
                        <div class="comp-stat">
                            <span class="comp-stat-val">{{ matchesCount }}</span>
                            <span class="comp-stat-lbl">{{ $t('statistics.matches_count') }}</span>
                        </div>
                        <div class="comp-stat">
                            <span class="comp-stat-val">{{ teamPlayers.length }}</span>
                            <span class="comp-stat-lbl">{{ $t('statistics.players') }}</span>
                        </div>
                    </div>
                    <div class="comp-summary-sep" />
                    <div class="comp-stat-row">
                        <div class="comp-stat">
                            <span class="comp-stat-val" :style="{ color: colors.teal2 }">{{ avgPlayed }}'</span>
                            <span class="comp-stat-lbl">{{ $t('statistics.avg_minutes_short') }}</span>
                        </div>
                        <div class="comp-stat">
                            <span class="comp-stat-val" :style="{ color: rpeMatches.length ? rpeColor(rpeAvg) : null }">{{ rpeMatches.length ? rpeAvg : '—' }}</span>
                            <span class="comp-stat-lbl">{{ $t('statistics.avg_rpe_short') }}</span>
                        </div>
                        <div class="comp-stat">
                            <span class="comp-stat-val" :style="{ color: wellMatches.length ? wellColor(wellAvg) : null }">{{ wellMatches.length ? wellAvg : '—' }}</span>
                            <span class="comp-stat-lbl">{{ $t('statistics.avg_wellness_short') }}</span>
                        </div>
                    </div>
                </div>
                <div v-else class="comp-empty">{{ $t('common.without_data') }}</div>
            </stat-card>

            <stat-card v-else-if="hasPeers" expandable :expanded="expandedCard === 5" @toggle:expand="toggleExpand(5)">
                <template #header>
                    <span>{{ peerTitle }}</span>
                </template>
                <skeleton-loader v-if="loadingTeam" type="team-bars" class="comp-team" />
                <template v-else-if="teamBars.length">
                    <div class="comp-team">
                        <div v-for="row in teamBars" :key="row.studentId" class="comp-team-row"
                            :class="{ current: row.isCurrent }">
                            <span class="comp-team-name">
                                <truncated-text :text="row.name" />
                            </span>
                            <div class="comp-team-bar-wrap">
                                <span v-for="p in percentileMarks" :key="p.key" class="comp-team-pline"
                                    :style="{ left: p.pos + '%' }" />
                                <div class="comp-team-bar" :style="{ width: teamBarWidth(row) }" />
                            </div>
                            <span class="comp-team-val">{{ row.minutes }}'</span>
                        </div>
                    </div>
                    <div v-if="percentileMarks.length" class="comp-legend">
                        <div v-for="p in percentileMarks" :key="p.key" class="comp-leg-item">
                            <span class="comp-leg-dash" />{{ p.label }}
                        </div>
                    </div>
                </template>
                <div v-else class="comp-empty">{{ $t('statistics.no_team_data') }}</div>
            </stat-card>
        </stats-grid>
    </div>
</template>

<script>
import LineChart from '../common/LineChart.vue'
import DoughnutChart from '../common/DoughnutChart.vue'
import TruncatedText from '../common/TruncatedText.vue'
import SkeletonLoader from '../common/SkeletonLoader.vue'
import StatCard from './StatCard.vue'
import StatsGrid from './StatsGrid.vue'
import { sumWellnessPreInfo, rpeColor, wellColor } from '../../utils/Routine'
import { fetchStudentsAdvances } from '@/utils/Statistics'

const COLORS = {
    teal: '#339999',
    teal2: '#66CCCC',
    lime: '#CCFF33',
    amber: '#F5C842',
    red: '#E85C5C',
    green: '#009966',
    muted: '#5A6677',
}

const RPE_ZONES = [
    { from: 0, to: 5, color: 'rgba(0, 153, 102, 0.06)' },
    { from: 5, to: 8, color: 'rgba(245, 200, 66, 0.06)', line: 'rgba(245, 200, 66, 0.5)' },
    { from: 8, to: 10, color: 'rgba(232, 92, 92, 0.07)', line: 'rgba(232, 92, 92, 0.5)' },
]

const WELLNESS_ZONES = [
    { from: 5, to: 12, color: 'rgba(232, 92, 92, 0.07)' },
    { from: 12, to: 18, color: 'rgba(245, 200, 66, 0.06)', line: 'rgba(245, 200, 66, 0.5)' },
    { from: 18, to: 25, color: 'rgba(51, 153, 153, 0.07)', line: 'rgba(51, 153, 153, 0.5)' },
]

export default {
    name: 'StatisticsCompetences',
    components: { LineChart, DoughnutChart, TruncatedText, SkeletonLoader, StatCard, StatsGrid },
    props: {
        docs: {
            type: Array,
            default: () => []
        },
        startDate: {
            type: String,
            default: null
        },
        endDate: {
            type: String,
            default: null
        },
        studentId: {
            type: String,
            default: null
        },
        students: {
            type: Array,
            default: () => []
        },
        teamId: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            colors: COLORS,
            rpeZones: RPE_ZONES,
            wellnessZones: WELLNESS_ZONES,
            chartStyles: { position: 'relative', height: '100%', width: '100%' },
            ringStyles: { position: 'relative', height: '100%', width: '100%' },
            teamMinutes: [],
            loadingTeam: false,
            teamCache: { key: null, data: null },
            expandedCard: null,
        }
    },
    computed: {
        pointBorder(vm = this) {
            return vm.$vuetify.theme.currentTheme.bg
        },
        isTeamView(vm = this) {
            return !!vm.teamId
        },
        matches(vm = this) {
            return (vm.docs || [])
                .slice()
                .sort((a, b) => vm.$moment(a?.rawDate?.full).toDate() - vm.$moment(b?.rawDate?.full).toDate())
                .map(doc => {
                    const post = doc?.advance?.postInfo || null
                    const opp = String(doc?.versus || '').trim() || '—'
                    return {
                        id: doc.id,
                        date: doc?.rawDate?.full || null,
                        dateLabel: doc?.rawDate?.full ? vm.$moment(doc.rawDate.full).format('DD MMM').replace('.', '') : '—',
                        opp,
                        abbr: vm.abbr(opp),
                        position: String(post?.position || '').trim() || null,
                        minMatch: vm.num(doc?.minutes),
                        minPlayed: vm.num(post?.duration),
                        rpe: doc?.advance?.post ? vm.num(post?.effort) : null,
                        well: doc?.advance?.pre ? sumWellnessPreInfo(doc?.advance?.preInfo) : null,
                        players: (doc?.assignedRoutines || []).filter(routine => routine?.advance?.post).length,
                    }
                })
        },
        teamPlayers(vm = this) {
            if (!vm.isTeamView) return []

            const totals = new Map()
            ;(vm.docs || []).forEach(doc => {
                (doc?.assignedRoutines || []).forEach(routine => {
                    const student = routine?.student
                    const id = student?.objectID
                    if (!id) return
                    const entry = totals.get(id) || { studentId: id, name: vm.shortName(student.name), minutes: 0 }
                    if (routine?.advance?.post) entry.minutes += vm.num(routine.advance.postInfo?.duration)
                    totals.set(id, entry)
                })
            })

            return [...totals.values()].sort((a, b) => b.minutes - a.minutes)
        },
        teamPlayerMax(vm = this) {
            return Math.max(1, ...vm.teamPlayers.map(p => p.minutes))
        },
        teamPlayerMarks(vm = this) {
            return vm.percentileMarksFor(vm.teamPlayers.map(p => p.minutes), vm.teamPlayerMax)
        },
        squadTotalMinutes(vm = this) {
            return Math.round(vm.teamPlayers.reduce((s, p) => s + p.minutes, 0))
        },
        history(vm = this) {
            return [...vm.matches].reverse()
        },
        totalPlayed(vm = this) {
            return vm.matches.reduce((s, m) => s + m.minPlayed, 0)
        },
        totalMatchMin(vm = this) {
            return vm.matches.reduce((s, m) => s + m.minMatch, 0)
        },
        matchesCount(vm = this) {
            return vm.matches.length
        },
        avgPlayed(vm = this) {
            return vm.matchesCount ? Math.round(vm.totalPlayed / vm.matchesCount) : 0
        },
        participationPct(vm = this) {
            return vm.totalMatchMin > 0 ? Math.round(vm.totalPlayed / vm.totalMatchMin * 100) : 0
        },
        participationLabels(vm = this) {
            return [vm.$t('statistics.played'), vm.$t('statistics.not_played')]
        },
        participationDatasets(vm = this) {
            const remaining = Math.max(0, vm.totalMatchMin - vm.totalPlayed)
            return [{
                data: [vm.totalPlayed, remaining],
                backgroundColor: [COLORS.teal, vm.$vuetify.theme.currentTheme.surface2],
                borderWidth: 0,
            }]
        },
        ringCenterLines(vm = this) {
            const theme = vm.$vuetify.theme.currentTheme
            return [
                { text: `${vm.participationPct}%`, fontSize: 24, fontWeight: 800, color: theme.txtPrimary },
                { text: vm.$t('statistics.of_time'), fontSize: 10, fontWeight: 500, color: theme.txtMuted },
                { text: `${vm.totalPlayed}' / ${vm.totalMatchMin}'`, fontSize: 9, fontWeight: 500, color: theme.txtMuted },
            ]
        },
        rpeMatches(vm = this) {
            return vm.matches.filter(m => m.rpe != null)
        },
        rpeAvg(vm = this) {
            if (!vm.rpeMatches.length) return 0
            return Number((vm.rpeMatches.reduce((s, m) => s + m.rpe, 0) / vm.rpeMatches.length).toFixed(1))
        },
        rpeChart(vm = this) {
            return {
                labels: vm.rpeMatches.map(m => m.abbr),
                datasets: [{
                    label: vm.$t('statistics.effort'),
                    dates: vm.rpeMatches.map(m => `${m.dateLabel} · ${m.opp}`),
                    data: vm.rpeMatches.map(m => m.rpe),
                    borderColor: COLORS.muted,
                    borderWidth: 1.5,
                    fill: false,
                    lineTension: 0,
                    pointRadius: 5,
                    pointHoverRadius: 6,
                    pointBackgroundColor: vm.rpeMatches.map(m => vm.rpeColor(m.rpe)),
                    pointBorderColor: vm.pointBorder,
                    pointBorderWidth: 1.5,
                }]
            }
        },
        wellMatches(vm = this) {
            return vm.matches.filter(m => m.well != null)
        },
        wellAvg(vm = this) {
            if (!vm.wellMatches.length) return 0
            return vm.round1(vm.wellMatches.reduce((s, m) => s + m.well, 0) / vm.wellMatches.length)
        },
        wellChart(vm = this) {
            return {
                labels: vm.wellMatches.map(m => m.abbr),
                datasets: [{
                    label: vm.$t('statistics.wellness'),
                    dates: vm.wellMatches.map(m => `${m.dateLabel} · ${m.opp}`),
                    data: vm.wellMatches.map(m => m.well),
                    borderColor: COLORS.muted,
                    borderWidth: 1.5,
                    fill: false,
                    lineTension: 0,
                    pointRadius: 5,
                    pointHoverRadius: 6,
                    pointBackgroundColor: vm.wellMatches.map(m => vm.wellColor(m.well)),
                    pointBorderColor: vm.pointBorder,
                    pointBorderWidth: 1.5,
                }]
            }
        },
        selectedStudent(vm = this) {
            return vm.students.find(s => s?.objectID === vm.studentId) || null
        },
        peerType(vm = this) {
            if (vm.selectedStudent?.team?.id) return 'team'
            if (vm.selectedStudent?.group) return 'group'
            return null
        },
        peerId(vm = this) {
            if (vm.peerType === 'team') return vm.selectedStudent.team.id
            if (vm.peerType === 'group') return vm.selectedStudent.group
            return null
        },
        peers(vm = this) {
            if (vm.peerType === 'team') return vm.students.filter(s => s?.team?.id === vm.peerId)
            if (vm.peerType === 'group') return vm.students.filter(s => s?.group === vm.peerId)
            return []
        },
        hasPeers(vm = this) {
            return vm.peers.length > 1
        },
        cardCount(vm = this) {
            return vm.isTeamView || vm.hasPeers ? 6 : 5
        },
        peerTitle(vm = this) {
            return vm.peerType === 'group' ? vm.$t('statistics.minutes_vs_group') : vm.$t('statistics.minutes_vs_team')
        },
        teamQueryKey(vm = this) {
            return vm.hasPeers ? `${vm.peerType}|${vm.peerId}|${vm.startDate}|${vm.endDate}` : null
        },
        teamBars(vm = this) {
            return vm.teamMinutes
                .map(row => ({ ...row, isCurrent: row.studentId === vm.studentId }))
                .filter(row => row.minutes > 0 || row.isCurrent)
        },
        teamMax(vm = this) {
            return Math.max(1, ...vm.teamBars.map(t => t.minutes))
        },
        percentileMarks(vm = this) {
            return vm.percentileMarksFor(vm.teamBars.map(t => t.minutes), vm.teamMax)
        },
    },
    methods: {
        percentileMarksFor(values, max) {
            const vm = this
            if (values.length < 3) return []
            const sorted = [...values].sort((a, b) => a - b)
            return [
                { key: 'p25', label: 'P25', value: vm.percentile(sorted, 0.25) },
                { key: 'p50', label: 'P50', value: vm.percentile(sorted, 0.5) },
                { key: 'p75', label: 'P75', value: vm.percentile(sorted, 0.75) },
            ].map(p => ({ ...p, pos: Math.min(100, p.value / max * 100) }))
        },
        round(value) {
            return Math.round(Number(value) || 0)
        },
        round1(value) {
            return Number((Number(value) || 0).toFixed(1))
        },
        playerBarWidth(row) {
            return `${Math.max(2, row.minutes / this.teamPlayerMax * 100)}%`
        },
        toggleExpand(index) {
            const vm = this
            vm.expandedCard = vm.expandedCard === index ? null : index
        },
        num(value) {
            const n = Number(value)
            return Number.isFinite(n) ? n : 0
        },
        abbr(name) {
            const s = String(name || '').trim()
            if (!s || s === '—') return '—'
            return s.replace(/\s+/g, '').slice(0, 3).toUpperCase()
        },
        shortName(name) {
            const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
            if (parts.length <= 1) return parts[0] || '—'
            return `${parts[0]} ${parts[1][0]}.`
        },
        rpeColor,
        wellColor,
        minColor(m) {
            const pct = m.minMatch > 0 ? m.minPlayed / m.minMatch : 0
            return pct >= 0.8 ? COLORS.teal : pct >= 0.5 ? COLORS.teal2 : COLORS.muted
        },
        playedPct(m) {
            return m.minMatch > 0 ? Math.round(m.minPlayed / m.minMatch * 100) : 0
        },
        barFillHeight(m) {
            if (!m.minMatch) return '0%'
            return `${Math.min(100, Math.max(0, m.minPlayed / m.minMatch * 100))}%`
        },
        teamBarWidth(row) {
            return `${Math.max(2, row.minutes / this.teamMax * 100)}%`
        },
        percentile(sortedAsc, p) {
            if (!sortedAsc.length) return 0
            const idx = (sortedAsc.length - 1) * p
            const lo = Math.floor(idx)
            const hi = Math.ceil(idx)
            if (lo === hi) return sortedAsc[lo]
            return sortedAsc[lo] + (sortedAsc[hi] - sortedAsc[lo]) * (idx - lo)
        },
        async loadTeamMinutes() {
            const vm = this
            if (!vm.hasPeers || !vm.startDate || !vm.endDate) {
                vm.teamMinutes = []
                return
            }
            const key = vm.teamQueryKey
            if (vm.teamCache.key === key && vm.teamCache.data) {
                vm.teamMinutes = vm.teamCache.data
                return
            }

            vm.loadingTeam = true
            try {
                const advances = await fetchStudentsAdvances({
                    teamId: vm.peerType === 'team' ? vm.peerId : null,
                    groupId: vm.peerType === 'group' ? vm.peerId : null,
                    start: vm.startDate,
                    end: vm.endDate,
                    competence: true,
                })
                const totals = {}
                advances.forEach(a => {
                    const sid = a.studentId
                    if (!sid) return
                    totals[sid] = (totals[sid] || 0) + vm.num(a?.advance?.postInfo?.duration)
                })

                const data = vm.peers
                    .map(s => ({
                        studentId: s.objectID,
                        name: vm.shortName(s.name),
                        minutes: totals[s.objectID] || 0,
                    }))
                    .sort((a, b) => b.minutes - a.minutes)

                vm.teamMinutes = data
                vm.teamCache = { key, data }
            } catch (err) {
                console.log('loadTeamMinutes error', err)
                vm.teamMinutes = []
            } finally {
                vm.loadingTeam = false
            }
        },
    },
    watch: {
        teamQueryKey: {
            immediate: true,
            handler() {
                this.loadTeamMinutes()
            }
        }
    }
}
</script>

<style scoped>
.comp-section {
    display: flex;
    flex-direction: column;
    min-height: 0;
}


.comp-card--wide {
    grid-column: span 2;
}

.comp-hdr-avg {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: normal;
    text-transform: none;
    color: var(--v-txtMuted-base);
}

.comp-hdr-avg b {
    font-weight: 700;
}


.comp-empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    color: var(--v-txtMuted-base);
}

.comp-chart-wrap {
    flex: 1 1 auto;
    min-height: 0;
    position: relative;
}

.comp-bars {
    flex: 1;
    display: flex;
    align-items: stretch;
    justify-content: center;
    gap: 6px;
    min-height: 0;
}

.comp-bar-slot {
    flex: 1;
    min-width: 0;
    display: flex;
}

.comp-bar-col {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    height: 100%;
    min-width: 0;
    cursor: default;
}

.comp-bar-val {
    font-size: 9px;
    font-weight: 700;
    color: var(--v-txtSecondary-base);
    margin-bottom: 2px;
    flex-shrink: 0;
}

.comp-bar-track {
    position: relative;
    flex: 1;
    min-height: 0;
    width: 58%;
    max-width: 26px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 4px;
    overflow: hidden;
}

.comp-bar-fill {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 4px;
    opacity: 0.9;
}

.comp-bar-lbl {
    font-size: 8px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
    margin-top: 5px;
    flex-shrink: 0;
}

.comp-legend {
    display: flex;
    gap: 12px;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 8px;
    flex-wrap: wrap;
}

.comp-leg-item {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 9px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
}

.comp-leg-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
}

.comp-leg-sq {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    flex-shrink: 0;
}

.comp-leg-sq--track {
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
}

.comp-leg-dash {
    width: 12px;
    height: 0;
    border-top: 1px dashed var(--v-primary-base);
    flex-shrink: 0;
}

.comp-ring {
    position: relative;
    flex: 1;
    min-height: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.comp-stat-row {
    display: flex;
    justify-content: center;
    gap: 22px;
    flex-shrink: 0;
    padding-top: 4px;
}

.comp-stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
}

.comp-stat-val {
    font-size: 18px;
    font-weight: 800;
    color: var(--v-txtPrimary-base);
    line-height: 1;
}

.comp-stat-lbl {
    font-size: 9px;
    font-weight: 500;
    color: var(--v-txtMuted-base);
}

.comp-summary {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 16px;
    min-height: 0;
}

.comp-summary-sep {
    height: 1px;
    background: var(--v-outline-base);
}

.comp-hist-wrap {
    flex: 1;
    min-height: 0;
    overflow: auto;
}

.comp-hist {
    width: 100%;
    border-collapse: collapse;
}

.comp-hist thead th {
    position: sticky;
    top: 0;
    background: var(--v-surface2-base);
    padding: 9px 14px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--v-txtMuted-base);
    border-bottom: 1px solid var(--v-outline-base);
    text-align: left;
    white-space: nowrap;
}

.comp-hist tbody td {
    padding: 9px 14px;
    font-size: 12px;
    font-weight: 500;
    color: var(--v-txtSecondary-base);
    border-bottom: 1px solid var(--v-outline-base);
    vertical-align: middle;
    white-space: nowrap;
}

.comp-hist tbody tr:last-child td {
    border-bottom: none;
}

.comp-hist tbody tr:hover td {
    background: var(--v-surface2-base);
}

.comp-hist td.comp-hist-opp {
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.comp-pos {
    display: inline-block;
    font-size: 9px;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 4px;
    background: rgba(51, 153, 153, 0.18);
    color: var(--v-secondary-base);
    letter-spacing: 0.03em;
}

.comp-hist-well {
    display: inline-flex;
    align-items: center;
    gap: 5px;
}

.comp-team {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 7px;
    padding-right: 2px;
}

.comp-team-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.comp-team-name {
    flex: 0 0 68px;
    max-width: 68px;
    text-align: right;
    font-size: 9px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
    overflow: hidden;
}

.comp-team-row.current .comp-team-name {
    color: var(--v-secondary-base);
    font-weight: 700;
}

.comp-team-bar-wrap {
    position: relative;
    flex: 1;
    height: 15px;
    background: var(--v-bg-base);
    border-radius: 4px;
    overflow: hidden;
}

.comp-team-bar {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    border-radius: 4px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
}

.comp-team-row.current .comp-team-bar,
.comp-team-bar--filled {
    background: linear-gradient(90deg, rgba(51, 153, 153, 0.5), rgba(102, 204, 204, 0.85));
    border: none;
}

.comp-team-pline {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 0;
    border-left: 1px dashed rgba(204, 255, 51, 0.5);
    z-index: 1;
}

.comp-team-val {
    flex: 0 0 40px;
    text-align: right;
    font-size: 9px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
}

.comp-team-row.current .comp-team-val {
    color: var(--v-secondary-base);
    font-weight: 700;
}

@media (max-width: 700px) {
    .comp-card--wide {
        grid-column: span 1;
    }
}
</style>

<style>
.comp-tt.v-tooltip__content {
    opacity: 1 !important;
    padding: 10px 12px;
    border-radius: 10px;
    border: 1px solid var(--v-outline-base);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.28);
}

.comp-tt-inner {
    font-family: 'Outfit', sans-serif;
    min-width: 130px;
}

.comp-tt-date {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--v-txtMuted-base);
    margin-bottom: 6px;
}

.comp-tt-row {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: var(--v-txtSecondary-base);
}

.comp-tt-row + .comp-tt-row {
    margin-top: 3px;
}

.comp-tt-row b {
    margin-left: auto;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.comp-tt-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}

.comp-tt-dot--track {
    border-radius: 2px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
}
</style>
